/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Timeline panel. It assembles the toolbar, ruler, label column and keyframe canvas, keeps
 * them in step with the project, selection, zoom and playback, and handles zooming and scrolling.
 */
import { canAddProperty, setKeyframe } from '../../../core/timeline/keyframes';
import { propertiesForTag } from '../../../core/properties/registry';
import { preferences } from '../../preferences';
import type { PlaybackController } from '../../playback';
import { readStorage, writeStorage } from '../../storage';
import { Component } from '../../ui/component';
import { h } from '../../ui/dom';
import { createInspectorContext } from '../inspector/inspector-context';
import type { StagePanel } from '../stage/stage';
import { showContextMenu, type MenuItem } from './context-menu';
import type { TimelineHost } from './host';
import { LabelColumn } from './label-column';
import { buildMarks, buildRows, type KeyMark, type TimelineRow } from './layout';
import { RulerView } from './ruler-view';
import { clampTimelineZoom, fitZoom, TIMELINE_PAD, timeToX, xToTime } from './ruler';
import type { OffsetPopover } from '../../dialogs/offset-popover';
import type { PresetsOpener } from '../../dialogs/lazy-dialogs';
import { TrackCanvas } from './track-canvas';
import { setTimelineView } from '../../store/timeline-actions';

export interface TimelineProps {
  playback: PlaybackController;
  stage: StagePanel;
  presets: PresetsOpener;
  offset: OffsetPopover;
}

const LABELS_KEY = 'svg-tempo:timeline-labels';
const END_PADDING_PX = 80;

export class Timeline extends Component<TimelineProps> {
  private rows: TimelineRow[] = [];
  private marks: KeyMark[] = [];
  private collapsed = new Set<string>();
  private pendingDuration: number | null = null;
  private fitPending = true;
  private labelColumn!: LabelColumn;
  private ruler!: RulerView;
  private canvas!: TrackCanvas;
  private body!: HTMLElement;
  private emptyNote!: HTMLElement;
  private durationLabel!: HTMLElement;
  private snapButton!: HTMLButtonElement;
  private offsetButton!: HTMLButtonElement;
  private host!: TimelineHost;
  private width = 0;
  private lastTick = 0;

  /** Creates the panel section. */
  protected override createRoot(): HTMLElement {
    return h('section', { class: ['panel', 'panel-timeline'], 'aria-label': 'Timeline' });
  }

  /** Builds the layout and subscribes to the store and playback. */
  protected override init(): void {
    const { playback, stage, presets, offset } = this.props;
    this.host = {
      store: this.store,
      playback,
      presets,
      offset,
      inspector: createInspectorContext(this.store, playback, stage),
      getView: () => {
        const { timelineZoom, timelineScroll } = this.store.getState().editor;
        return { zoom: timelineZoom, scroll: timelineScroll };
      },
      setView: (zoom, scroll) => {
        this.applyView(zoom, scroll);
      },
      getWidth: () => this.width,
      getRows: () => this.rows,
      getMarks: () => this.marks,
      getPendingDuration: () => this.pendingDuration,
      setPendingDuration: (ms) => {
        this.pendingDuration = ms;
        this.redraw();
      },
      requestDraw: () => {
        this.redraw();
      },
    };

    this.labelColumn = new LabelColumn(this.host, this.collapsed, (id) => {
      if (this.collapsed.has(id)) this.collapsed.delete(id);
      else this.collapsed.add(id);
      this.rebuild();
    });
    this.ruler = new RulerView(this.host);
    this.canvas = new TrackCanvas(this.host);

    const labelsWidth = Number(readStorage(LABELS_KEY));
    if (labelsWidth >= 160 && labelsWidth <= 360)
      this.el.style.setProperty('--timeline-labels', `${labelsWidth}px`);

    this.emptyNote = h('p', { class: 'panel-empty tl-empty' });
    this.body = h(
      'div',
      { class: 'tl-body' },
      h('div', { class: 'tl-body-inner' }, this.labelColumn.el, this.canvas.el),
      this.emptyNote,
    );
    this.el.append(
      h('h2', null, 'Timeline'),
      this.buildToolbar(),
      h(
        'div',
        { class: 'tl-header' },
        h('div', { class: 'tl-header-labels' }, this.buildSplitter()),
        this.ruler.el,
      ),
      this.body,
    );

    this.bindWheel();
    this.bindObservers();
    this.bindStore();
    this.rebuild();
  }

  /** Builds the toolbar: add menu, snapping, zoom, scaling preference and duration readout. */
  private buildToolbar(): HTMLElement {
    const add = h('button', { type: 'button', 'aria-haspopup': 'menu' }, '+ Add');
    add.addEventListener('click', () => {
      const rect = add.getBoundingClientRect();
      showContextMenu(rect.left, rect.bottom + 2, this.addMenu());
    });
    this.snapButton = h(
      'button',
      {
        type: 'button',
        'aria-pressed': 'true',
        title: 'Snap keyframes while dragging (hold Shift to suspend)',
      },
      'Snap',
    );
    this.snapButton.addEventListener('click', () => {
      const on = !this.store.getState().editor.snapping;
      this.store.updateEditor((draft) => {
        draft.snapping = on;
      });
    });
    const scale = h('input', {
      type: 'checkbox',
      checked: preferences.scaleKeyframes,
      id: 'timeline-scale',
    });
    scale.addEventListener('change', () => {
      preferences.scaleKeyframes = scale.checked;
    });
    this.durationLabel = h('span', { class: 'tl-duration' });
    const presetsButton = h('button', { type: 'button', title: 'Animation presets' }, 'Presets');
    presetsButton.addEventListener('click', () => {
      this.host.presets.open(presetsButton);
    });
    this.offsetButton = h(
      'button',
      { type: 'button', title: 'Offset animation across the selected elements', disabled: true },
      'Offset…',
    );
    this.offsetButton.addEventListener('click', () => {
      const box = this.offsetButton.getBoundingClientRect();
      this.host.offset.open(box.left, box.bottom + 4);
    });

    return h(
      'div',
      { class: 'tl-toolbar', role: 'toolbar', 'aria-label': 'Timeline controls' },
      add,
      presetsButton,
      this.offsetButton,
      this.snapButton,
      h(
        'button',
        {
          type: 'button',
          'aria-label': 'Zoom timeline out',
          title: 'Zoom out',
          onClick: () => {
            this.zoomBy(1 / 1.5);
          },
        },
        '−',
      ),
      h(
        'button',
        {
          type: 'button',
          'aria-label': 'Zoom timeline in',
          title: 'Zoom in',
          onClick: () => {
            this.zoomBy(1.5);
          },
        },
        '+',
      ),
      h(
        'button',
        {
          type: 'button',
          title: 'Fit the whole scene',
          onClick: () => {
            this.fit();
          },
        },
        'Fit',
      ),
      h(
        'label',
        { class: 'tl-scale', title: 'When the duration changes, scale keyframe times with it' },
        scale,
        ' Scale keyframes',
      ),
      h('span', { class: 'topbar-spacer' }),
      this.durationLabel,
    );
  }

  /** Builds the drag handle that resizes the label column. */
  private buildSplitter(): HTMLElement {
    const splitter = h('div', {
      class: 'tl-splitter',
      role: 'separator',
      'aria-label': 'Resize labels',
    });
    splitter.addEventListener('pointerdown', (down) => {
      down.preventDefault();
      splitter.setPointerCapture(down.pointerId);
      const startX = down.clientX;
      const startWidth = this.labelColumn.el.getBoundingClientRect().width;
      /** Resizes the column within its limits. */
      const move = (event: PointerEvent): void => {
        const width = Math.min(360, Math.max(160, startWidth + event.clientX - startX));
        this.el.style.setProperty('--timeline-labels', `${width}px`);
        writeStorage(LABELS_KEY, String(Math.round(width)));
      };
      /** Stops resizing. */
      const end = (): void => {
        splitter.removeEventListener('pointermove', move);
        splitter.removeEventListener('pointerup', end);
        splitter.removeEventListener('pointercancel', end);
      };
      splitter.addEventListener('pointermove', move);
      splitter.addEventListener('pointerup', end);
      splitter.addEventListener('pointercancel', end);
    });
    return splitter;
  }

  /** Menu of properties that can be keyed on the selected elements. */
  private addMenu(): MenuItem[] {
    const { editor, project } = this.store.getState();
    const ids = editor.selection.elementIds;
    const tags = ids.map((id) => project.elements[id]?.tag ?? '');
    if (ids.length === 0) return [{ label: 'Select an element first', disabled: true }];
    const available = propertiesForTag(tags[0] ?? '').filter((p) =>
      tags.every((tag) => p.appliesTo(tag.toLowerCase())),
    );
    return available.map((definition) => ({
      label: definition.cssOnly ? definition.label : `${definition.label} (JS)`,
      onSelect: () => {
        if (
          definition.key === 'morph' &&
          !ids.every((id) => this.host.inspector.shapes.ensurePath(id))
        ) {
          return;
        }
        const time = Math.round(this.props.playback.currentTime);
        this.store.updateProject('Add keyframe', (draft) => {
          for (const id of ids) {
            if (canAddProperty(draft, id, definition.key)) {
              setKeyframe(
                draft,
                id,
                definition.key,
                time,
                this.host.inspector.readValue(id, definition.key),
              );
            }
          }
        });
      },
    }));
  }

  /** Subscribes to everything the timeline displays. */
  private bindStore(): void {
    this.select(
      (s) => [s.project.tracks, s.editor.selection.elementIds, s.project.elements] as const,
      () => {
        this.rebuild();
      },
      { equality: 'shallow' },
    );
    this.select(
      (s) => s.editor.selection.elementIds.length,
      (count) => {
        this.offsetButton.disabled = count < 2;
      },
      { immediate: true },
    );
    this.select(
      (s) => [s.editor.timelineZoom, s.editor.timelineScroll] as const,
      () => {
        this.redraw();
        this.updatePlayhead(this.props.playback.currentTime);
      },
      { equality: 'shallow' },
    );
    this.select(
      (s) => [s.editor.selection.keyframeIds, s.editor.snapping, s.project.scene.duration] as const,
      ([, snapping, duration]) => {
        this.snapButton.setAttribute('aria-pressed', String(snapping));
        this.durationLabel.textContent = `Duration ${Number((duration / 1000).toFixed(3))}s`;
        this.redraw();
        this.updatePlayhead(this.props.playback.currentTime);
      },
      { equality: 'shallow', immediate: true },
    );
    this.select(
      (s) => [s.project, s.editor.playhead, s.editor.selection.elementIds] as const,
      () => {
        this.labelColumn.refreshStates();
      },
      { equality: 'shallow' },
    );
    this.addCleanup(
      this.props.playback.onTick((ms) => {
        this.updatePlayhead(ms);
        if (this.props.playback.isPlaying) this.followPlayhead(ms);
        const now = performance.now();
        if (now - this.lastTick > 1000 / 15) {
          this.lastTick = now;
          this.labelColumn.refreshStates();
        }
      }),
    );
  }

  /** Watches for size changes and vertical scrolling. */
  private bindObservers(): void {
    const observer = new ResizeObserver(() => {
      this.width = Math.max(0, Math.floor(this.canvas.el.clientWidth));
      this.canvas.setScrollWindow(this.body.scrollTop, this.body.clientHeight);
      if (this.fitPending && this.width > 0) this.fit();
      this.redraw();
      this.updatePlayhead(this.props.playback.currentTime);
    });
    observer.observe(this.canvas.el);
    observer.observe(this.body);
    this.addCleanup(() => {
      observer.disconnect();
    });
    this.listen(this.body, 'scroll', () => {
      this.canvas.setScrollWindow(this.body.scrollTop, this.body.clientHeight);
    });
  }

  /** Zoom with Ctrl + wheel around the cursor, and scroll horizontally with Shift + wheel. */
  private bindWheel(): void {
    this.listen(
      this.body,
      'wheel',
      (event) => {
        const view = this.host.getView();
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          const rect = this.canvas.el.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const time = xToTime(x, view);
          const zoom = clampTimelineZoom(
            view.zoom * 1.1 ** (-Math.max(-100, Math.min(100, event.deltaY)) / 100),
          );
          this.applyView(zoom, (time / 1000) * zoom + TIMELINE_PAD - x);
        } else if (event.shiftKey) {
          event.preventDefault();
          this.applyView(view.zoom, view.scroll + (event.deltaX || event.deltaY));
        }
      },
      { passive: false },
    );
  }

  /** Applies zoom and scroll, keeping scroll within the content. */
  private applyView(zoom: number, scroll: number): void {
    const clampedZoom = clampTimelineZoom(zoom);
    const duration = this.store.getState().project.scene.duration;
    const maxScroll = Math.max(
      0,
      (duration / 1000) * clampedZoom + END_PADDING_PX - this.width + TIMELINE_PAD,
    );
    setTimelineView(this.store, clampedZoom, Math.min(maxScroll, Math.max(0, scroll)));
  }

  /** Multiplies the zoom around the centre of the visible area. */
  private zoomBy(factor: number): void {
    const view = this.host.getView();
    const centre = this.width / 2;
    const time = xToTime(centre, view);
    const zoom = clampTimelineZoom(view.zoom * factor);
    this.applyView(zoom, (time / 1000) * zoom + TIMELINE_PAD - centre);
  }

  /** Fits the whole scene into the visible width. */
  private fit(): void {
    if (this.width === 0) return;
    this.fitPending = false;
    this.applyView(fitZoom(this.store.getState().project.scene.duration, this.width), 0);
  }

  /** During playback, scrolls by a page when the playhead leaves the visible range. */
  private followPlayhead(ms: number): void {
    const view = this.host.getView();
    const x = timeToX(ms, view);
    if (x > this.width - 10 || x < 0) {
      this.applyView(view.zoom, (ms / 1000) * view.zoom - 40 + TIMELINE_PAD);
    }
  }

  /** Moves the playhead line and ruler handle. */
  private updatePlayhead(ms: number): void {
    this.canvas.setPlayhead(ms);
    this.ruler.setPlayhead(ms);
  }

  /** Redraws the ruler and canvas. */
  private redraw(): void {
    this.ruler.draw();
    this.canvas.requestDraw();
  }

  /** Recomputes rows and marks from the project and refreshes the parts that show them. */
  private rebuild(): void {
    const { project, editor } = this.store.getState();
    this.rows = buildRows(project, editor.selection.elementIds, this.collapsed);
    this.marks = buildMarks(project, this.rows);
    this.labelColumn.render(this.rows);
    const animated = this.rows.some((row) => row.kind !== 'element');
    this.emptyNote.hidden = animated;
    this.emptyNote.textContent =
      this.rows.length === 0
        ? 'Select an element and click a diamond in the Inspector to start animating.'
        : 'Click a diamond in the Inspector to add the first keyframe.';
    this.canvas.modelChanged();
    this.durationLabel.textContent = `Duration ${Number((project.scene.duration / 1000).toFixed(3))}s`;
  }
}
